import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from 'react-router-dom';
import styled from 'styled-components';
import commonStyles from '../styles/common.module.css';
import Layout from '../components/layout/Layout';
import { useTelegram } from '../context/TelegramContext';
import { useAuth } from '../context/AuthContext';

// Импорт компонентов секций
import UserInfoSection from '../components/settings/UserInfoSection';
import LinkedAccountsSection from '../components/settings/LinkedAccountsSection';
import CurrencySection from '../components/settings/CurrencySection';
import SalaryDateSection from '../components/settings/SalaryDateSection';
import CategoryManagementSection from '../components/settings/CategoryManagementSection';
import AutomationSection from '../components/settings/AutomationSection';
import DisplaySettingsSection from '../components/settings/DisplaySettingsSection';
import NotificationsSection from '../components/settings/NotificationsSection';
import LanguageSection from '../components/settings/LanguageSection';
import BudgetManagementSection from '../components/settings/BudgetManagementSection';

const NoZoomContainer = styled.div`
  touch-action: pan-x pan-y; /* Запрещаем масштабирование, но разрешаем прокрутку */
  overflow-y: auto;
  height: 100%;
  width: 100%;
`;

const SettingsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px; // Уменьшено с 16px для соответствия стилю главной страницы
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: #4a6fa1;
  margin: 0;
  
  @media (max-width: 576px) {
    font-size: 20px;
  }
`;

// Стандартная кнопка назад, используемая на других страницах
const BackButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  padding: 8px 12px;
  background: linear-gradient(145deg, #f0f3f9, #e6ebf5);
  color: #4a6fa1;
  text-decoration: none;
  border-radius: 8px;
  font-weight: 500;
  font-size: 14px;
  box-shadow: 2px 2px 5px rgba(0, 0, 0, 0.05),
              -2px -2px 5px rgba(255, 255, 255, 0.8);
  transition: all 0.2s ease;
  
  &:hover, &:focus {
    background: linear-gradient(145deg, #e6ebf5, #dde2ec);
    transform: translateY(-2px);
    box-shadow: 3px 3px 7px rgba(0, 0, 0, 0.07),
                -3px -3px 7px rgba(255, 255, 255, 0.9);
  }
  
  &:active {
    transform: translateY(1px);
    box-shadow: inset 1px 1px 3px rgba(0, 0, 0, 0.1),
                inset -1px -1px 3px rgba(255, 255, 255, 0.9);
  }
`;

const Settings = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { telegramUserId } = useTelegram();
  const { isAuthenticated } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  
  useEffect(() => {
    // Находим или создаем метатег viewport
    let viewportMeta = document.querySelector('meta[name="viewport"]');
    const originalContent = viewportMeta ? viewportMeta.getAttribute('content') : '';
    
    if (!viewportMeta) {
      viewportMeta = document.createElement('meta');
      viewportMeta.name = 'viewport';
      document.head.appendChild(viewportMeta);
    }
    
    // Устанавливаем запрет масштабирования
    viewportMeta.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no');
    
    // Восстанавливаем оригинальное состояние при размонтировании компонента
    return () => {
      if (originalContent) {
        viewportMeta.setAttribute('content', originalContent);
      } else {
        viewportMeta.setAttribute('content', 'width=device-width, initial-scale=1.0');
      }
    };
  }, []);
  
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/');
    } else {
      setIsLoading(false);
    }
  }, [isAuthenticated, navigate]);

  if (isLoading) {
    return (
      <Layout>
        <NoZoomContainer>
          <div className={commonStyles.container}>
            <div className={commonStyles.loadingIndicator}>{t('loading')}</div>
          </div>
        </NoZoomContainer>
      </Layout>
    );
  }

  return (
    <Layout>
      <NoZoomContainer>
        <div className={commonStyles.container}>
          <PageHeader>
            <PageTitle>{t('settings')}</PageTitle>
            <BackButton to="/">
              {t('back_button')}
            </BackButton>
          </PageHeader>
          
          <SettingsContainer>
            <UserInfoSection userId={telegramUserId} />
            <LinkedAccountsSection />
            <CurrencySection />
            <SalaryDateSection />
            <CategoryManagementSection />
            <BudgetManagementSection />
            <AutomationSection />
            <DisplaySettingsSection />
            <NotificationsSection />
          </SettingsContainer>
        </div>
      </NoZoomContainer>
    </Layout>
  );
};

export default Settings;